import { useDropzone } from 'react-dropzone';
import { useState } from 'react';
import cx from 'classnames';
import PT from 'prop-types';
import { Button } from 'edrolo-style-guide';
import UploadedFile from './UploadedFile';
import UploadedFileReadOnly from './UploadedFileReadOnly';
import { UPLOAD_STATUS } from 'core/constants';
import LoadingIndicator from 'edrolo/domains/app/components/LoadingIndicator';
import { ErrorAlert } from 'core/components';
import { useFileUploader } from './hooks';
import styles from './styles.scss';

export const FileUploaderInput = ({
  blockId,
  onResponseChange,
  blockResponse = {},
  activityResponseId,
  readOnly,
  showExample,
}) => {
  const { uploadFile, uploadStatus, uploadError } = useFileUploader();
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);

  const onDrop = (acceptedFiles) => {
    acceptedFiles.map(async (file) => {
      // Request I. Upload file
      const response = await uploadFile(activityResponseId, blockId, file);

      // Request II. Record activity block response with file_key of the uploaded file in request I.
      await onResponseChange({
        file_key: response.fileKey,
        fileName: file.name,
        size: file.size,
        type: file.type,
      });
      setShowUploadConfirmation(true);

      setTimeout(() => {
        setShowUploadConfirmation(false);
      }, 3000);
    });
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    // The props are used based on reactDropzone documentation. More details at https://react-dropzone.js.org/#section-components

    noClick: true,
    noKeyboard: true,
    multiple: false,
    maxSize: 3e7,
    accept: {
      'image/*': [],
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        [],
    },
    onDrop,
  });

  const title =
    uploadStatus === UPLOAD_STATUS.ERROR ? (
      <div className={styles.errorAlert}>
        <ErrorAlert statusCode={uploadError?.status} />
      </div>
    ) : (
      <p>Select a file to upload</p>
    );

  return (
    <>
      {(readOnly || showExample || !showUploadConfirmation) &&
        blockResponse.file_key && (
          <div className={cx(styles.containerReadOnly, styles.spaceReadOnly)}>
            <div className={cx(styles.highlighted, styles.uploadConfirmation)}>
              File Upload:
            </div>
            <UploadedFileReadOnly
              fileName={blockResponse.fileName}
              blockId={blockId}
              activityResponseId={activityResponseId}
            />
          </div>
        )}

      {!(readOnly || showExample) &&
        uploadStatus !== UPLOAD_STATUS.UPLOADING &&
        !showUploadConfirmation && (
          <div {...getRootProps({ className: styles.container })}>
            <input {...getInputProps()} />
            {title}
            <div className={styles.button}>
              <Button small onClick={open}>
                Select a file
              </Button>
            </div>
            <p>
              You can upload the following file types: .doc, .docx, .pdf and
              image files.
            </p>
            <p>The maximum size allowed for file uploads is 30MB.</p>
          </div>
        )}

      {uploadStatus === UPLOAD_STATUS.UPLOADING && (
        <LoadingIndicator loadingText="Uploading..." />
      )}

      {!readOnly && blockResponse.file_key && showUploadConfirmation && (
        <UploadedFile
          fileName={blockResponse.fileName}
          blockId={blockId}
          activityResponseId={activityResponseId}
        />
      )}
    </>
  );
};

FileUploaderInput.propTypes = {
  blockId: PT.string.isRequired,
  onResponseChange: PT.func,
  blockResponse: PT.object,
  activityResponseId: PT.number.isRequired,
  readOnly: PT.bool,
  showExample: PT.bool,
};
